import { BREAKPOINTS } from '../utils/breakpoints.js';

function isSmartphone() {
  return window.innerWidth < BREAKPOINTS.pc_min;
}

function updateClippedSlides(swiper) {
  const slides = swiper.slides;
  const containerRect = swiper.el.getBoundingClientRect();
  const containerRight = containerRect.right;
  
  slides.forEach((slide) => {
    const slideRect = slide.getBoundingClientRect();
    const slideRight = slideRect.right;
    const slideLeft = slideRect.left;
    
    if (slideRight > containerRight && slideLeft < containerRight) {
      slide.classList.add('is-clipped');
    } else {
      slide.classList.remove('is-clipped');
    }
  });
}

function updateClippedSlidesForNextSlide(swiper) {
  const slides = swiper.slides;
  const containerRect = swiper.el.getBoundingClientRect();
  const containerRight = containerRect.right;
  
  const currentTranslate = swiper.getTranslate();
  const nextIndex = swiper.activeIndex + 1;
  
  let slideWidth = 0;
  if (slides[swiper.activeIndex]) {
    const slideRect = slides[swiper.activeIndex].getBoundingClientRect();
    slideWidth = slideRect.width + 32;
  }
  
  const predictedTranslate = currentTranslate - slideWidth;
  
  slides.forEach((slide) => {
    const slideRect = slide.getBoundingClientRect();
    
    const currentLeft = slideRect.left;
    const currentRight = slideRect.right;
    const predictedLeft = currentLeft - slideWidth;
    const predictedRight = currentRight - slideWidth;
    
    if (predictedRight > containerRight && predictedLeft < containerRight) {
      slide.classList.add('is-clipped');
    } else {
      slide.classList.remove('is-clipped');
    }
  });
}

function applyAccessibilityToClones(swiper) {
  const allSlides = swiper.slides;
  
  allSlides.forEach((slide) => {
    const isClone = slide.classList.contains('swiper-slide-duplicate');
    
    if (isClone) {
      slide.setAttribute('aria-hidden', 'true');
      const interactiveElements = slide.querySelectorAll('a, button');
      interactiveElements.forEach((element) => {
        element.setAttribute('tabindex', '-1');
      });
    } else {
      slide.removeAttribute('aria-hidden');
      const interactiveElements = slide.querySelectorAll('a, button');
      interactiveElements.forEach((element) => {
        element.removeAttribute('tabindex');
      });
    }
  });
}

function destroySlider(swiperContainer, swiperInstance) {
  // リサイズイベントリスナーを削除
  if (swiperContainer._resizeHandler) {
    window.removeEventListener('resize', swiperContainer._resizeHandler);
    delete swiperContainer._resizeHandler;
  }
  
  // リサイズタイマーをクリア
  if (swiperContainer._resizeTimer) {
    clearTimeout(swiperContainer._resizeTimer);
    delete swiperContainer._resizeTimer;
  }
  
  if (swiperInstance && swiperInstance.destroy) {
    swiperInstance.destroy(true, true);
  }
  
  const wrapper = swiperContainer.querySelector('.swiper-wrapper');
  if (wrapper) {
    // クローンを削除
    const clones = wrapper.querySelectorAll('.swiper-slide-duplicate');
    clones.forEach(clone => clone.remove());
    
    // swiper-slideクラスを削除
    const slides = wrapper.querySelectorAll('.swiper-slide');
    slides.forEach(slide => slide.classList.remove('swiper-slide'));
    
    // swiper-wrapperクラスを削除し、保存されていた元のクラスを復元
    wrapper.classList.remove('swiper-wrapper');
    if (wrapper.dataset.originalClasses) {
      const originalClasses = wrapper.dataset.originalClasses.split(' ');
      originalClasses.forEach(className => {
        if (className) wrapper.classList.add(className);
      });
      delete wrapper.dataset.originalClasses;
    } else {
      wrapper.classList.add('jsSlider');
    }
    
    // 初期化フラグを削除（再初期化を可能にする）
    delete wrapper.dataset.sliderInitialized;
    
    // swiperContainerを削除し、元の要素を親に戻す
    const parent = swiperContainer.parentElement;
    if (parent) {
      parent.insertBefore(wrapper, swiperContainer);
      parent.removeChild(swiperContainer);
    }
  }
}

export function initSlider() {
  const sliderElements = document.querySelectorAll('.jsSlider');
  
  if (!sliderElements.length || typeof Swiper === 'undefined') return;

  sliderElements.forEach((sliderElement) => {
    // 既に初期化済みの場合はスキップ（リサイズ時の再初期化を防ぐ）
    if (sliderElement.dataset.sliderInitialized === 'true') {
      return;
    }
    
    const slideCount = sliderElement.children.length;
    
    // --pickupモディファイアの有無をチェック
    const isPickup = sliderElement.classList.contains('jsSlider--pickup');
    
    // PC時に--pickupが付いている場合はスライダーを初期化しないが、リサイズイベントは設定する
    if (isPickup && !isSmartphone()) {
      sliderElement.dataset.sliderInitialized = 'true';
      
      // リサイズ時のデバウンス処理（SP⇄PC切り替えを検出）
      const handleResize = () => {
        clearTimeout(sliderElement._resizeTimer);
        sliderElement._resizeTimer = setTimeout(() => {
          const currentIsSmartphone = isSmartphone();
          
          // PCからSPに切り替わった場合、初期化フラグを削除して再初期化
          if (currentIsSmartphone) {
            delete sliderElement.dataset.sliderInitialized;
            if (sliderElement._resizeHandler) {
              window.removeEventListener('resize', sliderElement._resizeHandler);
              delete sliderElement._resizeHandler;
            }
            initSlider();
          }
        }, 150);
      };
      
      sliderElement._resizeHandler = handleResize;
      window.addEventListener('resize', handleResize);
      return;
    }
    
    const swiperContainer = document.createElement('div');
    swiperContainer.className = 'swiper';
    
    const parent = sliderElement.parentElement;
    parent.insertBefore(swiperContainer, sliderElement);
    
    // 元のクラス名を保存
    const originalClasses = Array.from(sliderElement.classList);
    sliderElement.dataset.originalClasses = originalClasses.join(' ');
    
    sliderElement.classList.remove('jsSlider');
    sliderElement.classList.add('swiper-wrapper');
    sliderElement.dataset.sliderInitialized = 'true';
    
    const originalSlides = Array.from(sliderElement.children);
    originalSlides.forEach(child => {
      child.classList.add('swiper-slide');
    });
    
    // SPの場合はスライド数に関係なく動作、PCの場合は4個以上の場合のみ動作
    const shouldEnableNavigation = isSmartphone() || slideCount >= 4;
    
    if (shouldEnableNavigation) {
      for (let i = 0; i < 4; i++) {
        originalSlides.forEach(slide => {
          const clone = slide.cloneNode(true);
          clone.classList.add('swiper-slide-duplicate');
          
          clone.setAttribute('aria-hidden', 'true');
          const interactiveElements = clone.querySelectorAll('a, button');
          interactiveElements.forEach((element) => {
            element.setAttribute('tabindex', '-1');
          });
          
          sliderElement.appendChild(clone);
        });
      }
      
      swiperContainer.appendChild(sliderElement);
      
      const nextButton = document.createElement('div');
      nextButton.className = 'swiper-button-next';
      swiperContainer.appendChild(nextButton);
      
      // --pickupの場合はspaceBetweenを16に、それ以外は32に設定
      const spaceBetweenValue = isPickup ? 16 : 32;
      
      const swiper = new Swiper(swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: spaceBetweenValue,
        loop: false,
        speed: 600,
        navigation: {
          nextEl: nextButton,
        },
        on: {
          init: function() {
            updateClippedSlides(this);
            applyAccessibilityToClones(this);
          },
          slideChangeTransitionStart: function() {
            updateClippedSlides(this);
            applyAccessibilityToClones(this);
          },
          setTranslate: function() {
            // モーダルが開いている間は、保存した位置を復元
            if (this._modalOpen && this._modalSavedTranslate !== undefined) {
              const currentTranslate = this.translate;
              // 位置が変わっている場合のみ復元（無限ループを防ぐ）
              if (Math.abs(currentTranslate - this._modalSavedTranslate) > 1) {
                // 直接translateプロパティを設定（setTranslateを呼ばない）
                this.translate = this._modalSavedTranslate;
                this.setTransition(0);
                this.wrapperEl.style.transform = `translate3d(${this._modalSavedTranslate}px, 0, 0)`;
                this.setTransition(600);
                return;
              }
            }
            // モーダルを閉じている間は、保存した位置を復元
            if (this._modalClosing && this._modalClosingTranslate !== undefined) {
              const currentTranslate = this.translate;
              // 位置が変わっている場合のみ復元（無限ループを防ぐ）
              if (Math.abs(currentTranslate - this._modalClosingTranslate) > 1) {
                // 直接translateプロパティを設定（setTranslateを呼ばない）
                this.translate = this._modalClosingTranslate;
                this.setTransition(0);
                this.wrapperEl.style.transform = `translate3d(${this._modalClosingTranslate}px, 0, 0)`;
                this.setTransition(600);
                return;
              }
            }
            updateClippedSlides(this);
          },
          slideChangeTransitionEnd: function() {
            const activeIndex = this.activeIndex;
            
            if (activeIndex >= slideCount * 2) {
              this.setTransition(0);
              this.slideTo(activeIndex - slideCount, 0);
              setTimeout(() => {
                this.setTransition(600);
              }, 50);
            }
            
            updateClippedSlides(this);
            applyAccessibilityToClones(this);
          },
        },
      });
      
      nextButton.addEventListener('click', () => {
        updateClippedSlidesForNextSlide(swiper);
      });
      
      setTimeout(() => {
        swiper.slideTo(slideCount, 0);
        setTimeout(() => {
          updateClippedSlides(swiper);
          applyAccessibilityToClones(swiper);
        }, 100);
      }, 100);
      
      // スライダーインスタンスを保存
      swiperContainer.dataset.swiperInstance = 'true';
      swiperContainer._swiperInstance = swiper;
      
      // リサイズ時のデバウンス処理
      const handleResize = () => {
        // スライダーが既に破棄されている場合は処理をスキップ
        if (!swiperContainer.parentElement || !swiperContainer._swiperInstance) {
          return;
        }
        
        clearTimeout(swiperContainer._resizeTimer);
        swiperContainer._resizeTimer = setTimeout(() => {
          // 再度チェック（タイマー実行中に破棄された可能性がある）
          if (!swiperContainer.parentElement || !swiperContainer._swiperInstance) {
            return;
          }
          
          const currentIsSmartphone = isSmartphone();
          const currentShouldEnableNavigation = currentIsSmartphone || slideCount >= 4;
          
          // --pickupの場合、PC時はスライダーを破棄する必要がある
          if (isPickup && !currentIsSmartphone) {
            destroySlider(swiperContainer, swiper);
            setTimeout(() => {
              initSlider();
            }, 100);
            return;
          }
          
          // SP/PCの切り替えを検出
          if (shouldEnableNavigation !== currentShouldEnableNavigation) {
            // スライダーを破棄して再初期化
            destroySlider(swiperContainer, swiper);
            // 少し遅延させて再初期化（リサイズが完了してから）
            setTimeout(() => {
              initSlider();
            }, 100);
          } else {
            updateClippedSlides(swiper);
          }
        }, 150);
      };
      
      // リサイズハンドラーを保存（後で削除できるように）
      swiperContainer._resizeHandler = handleResize;
      window.addEventListener('resize', handleResize);
    } else {
      swiperContainer.appendChild(sliderElement);
      
      // --pickupの場合はspaceBetweenを16に、それ以外は32に設定
      const spaceBetweenValue = isPickup ? 16 : 32;
      
      const swiper = new Swiper(swiperContainer, {
        slidesPerView: 'auto',
        spaceBetween: spaceBetweenValue,
        allowTouchMove: false,
        loop: false,
        on: {
          init: function() {
            updateClippedSlides(this);
            applyAccessibilityToClones(this);
          },
          setTranslate: function() {
            // モーダルが開いている間は、保存した位置を復元
            if (this._modalOpen && this._modalSavedTranslate !== undefined) {
              const currentTranslate = this.translate;
              // 位置が変わっている場合のみ復元（無限ループを防ぐ）
              if (Math.abs(currentTranslate - this._modalSavedTranslate) > 1) {
                // 直接translateプロパティを設定（setTranslateを呼ばない）
                this.translate = this._modalSavedTranslate;
                this.setTransition(0);
                this.wrapperEl.style.transform = `translate3d(${this._modalSavedTranslate}px, 0, 0)`;
                this.setTransition(600);
                return;
              }
            }
            // モーダルを閉じている間は、保存した位置を復元
            if (this._modalClosing && this._modalClosingTranslate !== undefined) {
              const currentTranslate = this.translate;
              // 位置が変わっている場合のみ復元（無限ループを防ぐ）
              if (Math.abs(currentTranslate - this._modalClosingTranslate) > 1) {
                // 直接translateプロパティを設定（setTranslateを呼ばない）
                this.translate = this._modalClosingTranslate;
                this.setTransition(0);
                this.wrapperEl.style.transform = `translate3d(${this._modalClosingTranslate}px, 0, 0)`;
                this.setTransition(600);
                return;
              }
            }
            updateClippedSlides(this);
          },
        },
      });
      
      // スライダーインスタンスを保存
      swiperContainer.dataset.swiperInstance = 'true';
      swiperContainer._swiperInstance = swiper;
      
      // リサイズ時のデバウンス処理
      const handleResize = () => {
        // スライダーが既に破棄されている場合は処理をスキップ
        if (!swiperContainer.parentElement || !swiperContainer._swiperInstance) {
          return;
        }
        
        clearTimeout(swiperContainer._resizeTimer);
        swiperContainer._resizeTimer = setTimeout(() => {
          // 再度チェック（タイマー実行中に破棄された可能性がある）
          if (!swiperContainer.parentElement || !swiperContainer._swiperInstance) {
            return;
          }
          
          const currentIsSmartphone = isSmartphone();
          const currentShouldEnableNavigation = currentIsSmartphone || slideCount >= 4;
          
          // --pickupの場合、PC時はスライダーを破棄する必要がある
          if (isPickup && !currentIsSmartphone) {
            destroySlider(swiperContainer, swiper);
            setTimeout(() => {
              initSlider();
            }, 100);
            return;
          }
          
          // SP/PCの切り替えを検出
          if (shouldEnableNavigation !== currentShouldEnableNavigation) {
            // スライダーを破棄して再初期化
            destroySlider(swiperContainer, swiper);
            // 少し遅延させて再初期化（リサイズが完了してから）
            setTimeout(() => {
              initSlider();
            }, 100);
          } else {
            updateClippedSlides(swiper);
          }
        }, 150);
      };
      
      // リサイズハンドラーを保存（後で削除できるように）
      swiperContainer._resizeHandler = handleResize;
      window.addEventListener('resize', handleResize);
    }
  });
}

